// Provenance: repo:tds-reconciliation-portal:frontend/src/components/MatchWorkbench.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings.
//
// Matching, inside the section that counts the work. The expanded one-sided
// panel used to be a party table whose Books columns were structurally empty —
// dead width sitting exactly where the matching belongs — so the section's own
// names became the primary list and the other side a ranked, multi-selectable
// secondary. Both lists are `NameColumn`, both fed by `sideOptions`, so the
// two columns here and the two in `AliasPicker` cannot come to different
// answers about which names exist or what they are worth.
import { useState } from 'react'

import { useAliasCandidates, useAliasPlan, useCreateAliasFromRun } from '../api/aliases'
import { ApiError } from '../api/client'
import { workbenchState } from '../api/nameMapping'
import type { MatchSide } from '../api/nameMapping'
import { AliasPlanPreview, ClosureLine, NameColumn } from './AliasPicker'
import { InfoTip } from './InfoTip'
import { Btn } from './Modal'
import { Money } from './Money'
import { showToast } from './toast'

const EMPTY = { names_26as: [], names_books: [] }

const TITLE: Record<MatchSide, string> = {
  '26as': '26AS deductor names',
  books: 'Books party names',
}

export function MatchWorkbench({
  runId,
  clientId,
  category,
}: {
  runId: string
  clientId?: string
  category: string
}) {
  const [primary, setPrimary] = useState<string | null>(null)
  const [secondary, setSecondary] = useState<string[]>([])
  const w = workbenchState(category, primary, secondary)
  // hooks before any early return — `w` is null only for a category the parent
  // should not have rendered this for
  const candidates = useAliasCandidates(runId, w?.against)
  const plan = useAliasPlan(runId, w?.selection ?? EMPTY)
  const create = useCreateAliasFromRun(runId, clientId ?? '')
  if (!w) return null

  const ranked = candidates.data?.against === w.against?.name
    ? (candidates.data?.ranked ?? undefined)
    : undefined
  // the picked name's own one-sided figure, read off the same list the column
  // renders so the band and the row beside it cannot quote different rupees
  const openOfPrimary = (w.primary === '26as'
    ? candidates.data?.one_sided_26as
    : candidates.data?.one_sided_books)?.find((o) => o.name === primary)?.tds ?? '0'

  // Changing the primary clears the ticks. They were candidates for a
  // different company; carrying them over would build an alias out of two
  // unrelated questions.
  const pick = (name: string) => {
    setPrimary((cur) => (cur === name ? null : name))
    setSecondary([])
  }
  const toggle = (name: string) =>
    setSecondary((cur) =>
      cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name])

  if (candidates.isPending) {
    return <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
  }
  if (candidates.isError) {
    return (
      <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
        Could not load this run&rsquo;s names: {String(candidates.error)}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {/* the how-to paragraph folds behind an ⓘ — read once on day one, it
          cost a full-width band on day two hundred. The line it rides on is
          the category's own name, so the panel spends ONE line on words. */}
      <div className="flex flex-nowrap items-center gap-1">
        <p className="text-xs font-semibold text-muted-foreground">{category}</p>
        <InfoTip>
          Pick a name on the left; the right re-orders by how like it each name
          is. The right column lists unmatched names first, then — below the
          divider — parties that already matched on both sides: tick one of
          those only to fold the left name into that party, which is the alias
          that actually closes a shortfall.
        </InfoTip>
      </div>
      {/* The gap band. Appears the moment a name is picked and answers the
          question the reconciler is actually holding: how much is this name
          short, and does what I have ticked cover it.

          Driven by `plan.closes`, NEVER by summing the ₹ beside the ticked
          names. In All-parties mode that figure is the name's PARTY total, not
          what joining it releases — a client-side total would report nothing
          covered on a join that closes the shortfall completely. */}
      {primary && (
        <div className="space-y-1.5 rounded-lg border border-border bg-muted/50 p-3">
          <p className="break-words text-sm font-semibold text-foreground">{primary}</p>
          <p className="text-xs text-muted-foreground">
            open in {w.primary === '26as' ? '26AS' : 'books'}{' '}
            <Money v={openOfPrimary} bold className="text-xs" />
          </p>
          {secondary.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Nothing ticked yet — pick the {w.secondary === 'books' ? 'books' : '26AS'} names
              that are this company.
            </p>
          ) : plan.data ? (
            <ClosureLine plan={plan.data} />
          ) : (
            <p className="text-xs text-muted-foreground">working out what this would close…</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <NameColumn
          title={TITLE[w.primary]}
          side={w.primary}
          data={candidates.data}
          selected={primary ? [primary] : []}
          onToggle={pick}
          mode="single"
          // this column IS the section's one-sided names; an All-parties
          // toggle here could only ever show names the section does not count
          scopeLock="orphans"
          // A-Z by default: the question you bring to this column is "are any
          // of these the same company twice", and the answer only shows when
          // the spellings sit together. Money is one click away.
          defaultSort="name"
        />
        <NameColumn
          title={TITLE[w.secondary]}
          side={w.secondary}
          data={candidates.data}
          selected={secondary}
          onToggle={toggle}
          ranked={ranked}
          against={w.against?.name}
          defaultScope="all"
        />
      </div>

      {/* Rendered before Create is reachable, never behind a disclosure:
          `create_group_from_run` records the reach signature at creation
          rather than sending the group round a second approval loop, and its
          justification is that the author read this list first. */}
      {w.ready && plan.data && <AliasPlanPreview plan={plan.data} />}

      <div className="flex items-center gap-2">
        <Btn
          kind="primary"
          disabled={!w.ready}
          loading={create.isPending}
          onClick={() =>
            create.mutate(w.selection, {
              onSuccess: (r) => {
                showToast({
                  color: 'green',
                  title: `Alias created: ${r.group.label}. It applies from the next run.`,
                })
                setPrimary(null)
                setSecondary([])
                // the mirror section, the category counts and the header cards
                // all refresh from `useCreateAliasFromRun`'s own invalidation
              },
              onError: (e) =>
                showToast({
                  color: 'red',
                  title: 'Could not create the alias',
                  message: e instanceof ApiError ? e.message : String(e),
                }),
            })
          }
        >
          Create alias
        </Btn>
        {(primary || secondary.length > 0) && (
          <Btn
            kind="subtle"
            onClick={() => {
              setPrimary(null)
              setSecondary([])
            }}
          >
            Clear
          </Btn>
        )}
      </div>
    </div>
  )
}
